import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "@images/wand-color.png";
import { css } from "@emotion/css";
import { useAuth } from "@contexts/AuthContext";
import { Flex } from "./Flex";
import { useCurrentSubscription } from "@hooks/query/useCurrentSubscription";
import { isCurrentUserAdmin } from "@utils/adminUtils";
import { getTokens } from "@utils/authStorage";

export const HEADER_Z_INDEX = 999;
export const HEADER_HEIGHT = 100;

const Header = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const { data: subscription, isLoading: subscriptionLoading } = useCurrentSubscription();

  const isMyLibraryActivated = location.pathname.includes("/my-library");
  const isColorActivated = location.pathname.includes("/color");
  const isAdminActivated = location.pathname.includes("/admin");
  const isPresetsAdminActivated = location.pathname.includes("/presets-admin");

  // Check if user has wand.tech email
  const { userData } = getTokens();
  const hasWandTechEmail = userData?.email?.endsWith('@wand.tech');

  const renderDefaultMenu = () => (
    <Flex align="center">
      <Link to="/careers">
        <button className={menuItemStyle}>Careers</button>
      </Link>
      <a href="https://twitter.com/wand_app" target="_blank" rel="noreferrer">
        <button className={menuItemStyle}>Twitter</button>
      </a>
      <a href="https://discord.gg/XA95uttmsV" target="_blank" rel="noreferrer">
        <button className={menuItemStyle}>Discord</button>
      </a>
      <a
        href="https://apps.apple.com/us/app/wand-ai-canvas-for-artists/id1635194640"
        target="_blank"
        rel="noreferrer"
      >
        <button className={menuCTAStyle}>Get Wand</button>
      </a>
    </Flex>
  );

  const renderAuthenticatedMenu = () => (
    <Flex align="center">
      <button
        className={iconMenuItemStyle}
        onClick={() => {
          navigate("/my-library");
        }}
      >
        <span className={iconStyle(isMyLibraryActivated)}>🖼️</span>
        <span className={textLabelStyle}>Library</span>
      </button>

      <button
        className={iconMenuItemStyle}
        onClick={() => {
          navigate("/color");
        }}
      >
        <span className={iconStyle(isColorActivated)}>🎨</span>
        <span className={textLabelStyle}>Color</span>
      </button>

      {isCurrentUserAdmin() && (
        <button
          className={iconMenuItemStyle}
          onClick={() => {
            navigate("/admin");
          }}
        >
          <span className={iconStyle(isAdminActivated)}>⚙️</span>
          <span className={textLabelStyle}>Admin</span>
        </button>
      )}

      {hasWandTechEmail && (
        <button
          className={iconMenuItemStyle}
          onClick={() => {
            navigate("/presets-admin");
          }}
        >
          <span className={iconStyle(isPresetsAdminActivated)}>🎯</span>
          <span className={textLabelStyle}>Presets</span>
        </button>
      )}

      <button 
        className={creditsButtonStyle}
        onClick={() => {
          navigate("/subscription");
        }}
        disabled={subscriptionLoading}
      >
        <span className={creditsIconStyle}>⭐</span>
        <span className={creditsTextStyle}>
          {subscriptionLoading ? "..." : (subscription?.image_credits ?? 0)}
        </span>
      </button>
    </Flex>
  );

  return (
    <>
      <Flex justify="center" className={wrapperStyle}>
        <div className={innerWrapperStyle}>
          <Link to="/">
            <img className={logoStyle} src={logo} loading="lazy" alt="Logo" />
          </Link>
          {loading
            ? null
            : isAuthenticated
            ? renderAuthenticatedMenu()
            : renderDefaultMenu()}
        </div>
      </Flex>

    </>
  );
};

const wrapperStyle = css`
  position: sticky;
  top: 0;
  z-index: ${HEADER_Z_INDEX};
  background-color: #000000;
`;

const innerWrapperStyle = css`
  box-sizing: border-box;
  max-width: 960px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 40px 0px 16px 16px;
  border-bottom: 1px solid #ffffff33;

  @media (max-width: 600px) {
    max-width: 100%;
    padding: 24px 16px;
  }
`;

const logoStyle = css`
  height: 40px;
  width: auto;
  object-fit: contain;

  @media (max-width: 480px) {
    height: 32px;
  }
`;

const menuItemStyle = css`
  color: #adadad;
  font-size: 18px;
  font-weight: 500;
  margin-right: 8px;
`;

const iconMenuItemStyle = css`
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  gap: 6px;
  display: flex;
  align-items: center;
  height: 40px;
  padding-right: 24px;
  box-sizing: border-box;

  @media (max-width: 480px) {
    width: 50px;
    padding-left: 0;
    justify-content: center;
  }
`;

const textLabelStyle = css`
  @media (max-width: 480px) {
    display: none;
  }
`;

const iconStyle = (isActive: boolean) => css`
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease-in-out;
  transform: ${isActive ? "scale(1.1)" : "scale(1)"};
  width: 20px;
  height: 20px;
`;

const menuCTAStyle = css`
  box-sizing: border-box;
  width: 120px;
  height: 40px;
  margin-left: 14px;
  background: #0f7dff;
  border-radius: 24px;
  color: #ffffff;
  font-weight: 500;
  font-size: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.3s ease-out, opacity 0.3s ease-out;

  &:hover {
    background-color: #449bff;
    opacity: 1;
  }
`;

const creditsButtonStyle = css`
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  gap: 6px;
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease-out;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    padding: 0 8px;
  }
`;

const creditsIconStyle = css`
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const creditsTextStyle = css`
  font-weight: 600;
  color: #ffffff;

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

export default Header;
